(ns minorodata.sandbox.api
  (:require
   [clojure.set :as c.set]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as utils :refer [assoc-some get-dag-id]]
   [minorodata.sandbox.events :refer [empty-tab]]
   [minorodata.sandbox.model :refer [->commit ->live-transform ->pipeline-dag
                                     ->pipeline-group ->pipeline-table
                                     ->pipeline-table-preview
                                     ->pipeline-transform
                                     ->pipeline-transform-preview
                                     ->pipeline-transform-search
                                     ->sandbox-preview ->sandbox-table
                                     ->sandbox-transform
                                     ->sandbox-transform-execution
                                     ->sandbox-updates ->sandbox-version]]
   [minorodata.transform.api :refer [select-tables]]
   [minorodata.transform.model :refer [->transform]]
   [mold-http.core :refer [reg-event-http]]
   [re-frame.core :refer [reg-event-fx]]
   [re-posh.core :as re-posh]))


(defn api-url [s & [m]]
  (reduce (fn [s [k v]] (str/replace s (str "{" (name k) "}") (js/encodeURIComponent (str v))))
          s m))


(defn fetch-live-table-request [_ [{:keys [params]}]]
  (let [[table schema catalog] (reverse (str/split (:id params) #"\."))
        catalog (or catalog @(re-posh/subscribe [:connections.subs/warehouse-db-name]))]
    {:http-xhrio
     {:method    :post
      :url       "/api/sink-tables"
      :params    {:catalog catalog
                  :schema  schema
                  :table   table}
      :cb-params {:params params}}}))

(defn fetch-live-table-callback [_ [error {:keys [data]} {:keys [params]}]]
  (if (some? error)
    (let [server-message (get-in error [:response :error])
          message (if (re-find #"SQL compilation error" server-message)
                    (str (:id params) ":\n" (str/replace server-message #"SQL compilation error:\n" ""))
                    (str "Failed to fetch warehouse " (:id params) " table metadata"))]
      {:dispatch [:app.events/add-snack :error message]})
    {:dispatch [:pipeline-table/add (->pipeline-table data)]}))


(reg-event-http
 :pipeline-editor.api/fetch-live-table
 fetch-live-table-request
 fetch-live-table-callback)


(defn fetch-live-table-preview-request [_ [{:keys [params]}]]
  {:http-xhrio
   {:id        :fetch-live-table-preview-request
    :method    :post
    :url       "/api/sink-console"
    :params    {:query (str "SELECT * FROM " (:id params) " LIMIT 1000;")}
    :cb-params {:params params}}
   #_{:method    :GET
      :url       (str "/api/sink-table/" (:id params) "/preview")
      :cb-params {:params params}}})

(defn fetch-live-table-preview-callback [_ [error {:keys [results]} {:keys [params]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (str "Failed to fetch warehouse " (:id params) " table preview")]}
    {:dispatch [:pipeline-table-preview/add (->pipeline-table-preview
                                             {:id      (:id params)
                                              :headers (ffirst results)
                                              :rows    (into [] (next (first results)))})]}))

(reg-event-http
 :pipeline-editor.api/fetch-live-table-preview
 fetch-live-table-preview-request
 fetch-live-table-preview-callback)


;;;;;;;;;;;;;;;
;; Transforms endpoints
;;

(defn fetch-transform-groups-request []
  {:http-xhrio
   {:id        :fetch-transform-groups-request
    :url       "/api/transformations"
    :params    {:pagination {:page 1 :items-per-page 1000}}}})

(defn fetch-transform-groups-callback [_ [error {:keys [results]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch transform groups"]}
    {:dispatch [:pipeline-group/add-many
                (map-indexed (fn [idx itm]
                               (->pipeline-group (assoc itm :order idx)))
                             results)]}))

(reg-event-http
 :pipeline-editor.api/fetch-transform-groups
 fetch-transform-groups-request
 fetch-transform-groups-callback)


(defn transform-search-request [_ [{:keys [params]}]]
  {:http-xhrio
   {:id        :pipeline-transform-search-request
    :url       "/api/transformations/search"
    :method    :POST
    :params    {:query (:query params)
                :specificity (or (:specificity params) "everything") ;; everything | titles | text
                :filters     {:type "transform"}
                :pagination  {:page 0 :items-per-page 50}}
    :cb-params {:params params}}})

(defn transform-search-callback [_ [error resp {:keys [params]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load transforms"]
                  [:app.api/log :error "Failed to load transforms" error]]}

    (let [{:keys [transforms]} resp
          transforms           (mapv (fn [transform]
                                       (->pipeline-transform transform))
                                     (:data transforms))]
      {:dispatch-n [[:pipeline-transform/add-many transforms]
                    [:pipeline-transform-search/update
                     (->pipeline-transform-search {:query      (:query params)
                                                   :transforms transforms})
                     {:replace true}]]})))

(reg-event-http
 :pipeline-editor.api/transform-search
 transform-search-request
 transform-search-callback)

(defn fetch-transforms-request [_ [{:keys [group-id on-success]}]]
  {:http-xhrio
   {:id        (str "fetch-pipeline-transforms-request-" group-id)
    :strategy  :keep-latest
    :url       (str "/api/transformations/" group-id "/transforms")
    :params    {:pagination {:page 1 :items-per-page 1000}}
    :cb-params {:group-id   group-id
                :on-success on-success}}})

(defn fetch-transforms-callback [_ [error resp {:keys [group-id on-success]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load transforms"]
                  [:app.api/log :error "Failed to load transforms" error]]}

    (let [{:keys [results]} resp
          transforms      (map-indexed (fn [idx transform]
                                         (->pipeline-transform (assoc transform :order idx)))
                                       results)]
      (when on-success (on-success results))
      {:dispatch-n [[:pipeline-transform/add-many transforms]
                    [:pipeline-group/update [:pipeline-group/id group-id]
                     {:pipeline-group/transforms transforms}]]})))

(reg-event-http
 :pipeline-editor.api/fetch-kioku-transforms
 fetch-transforms-request
 fetch-transforms-callback)


(defn fetch-pipeline-transform-request [_ [{{:keys [group-id transform-id]} :params}]]
  {:http-xhrio
   {:url       (str "/api/transformations/" group-id "/transforms/" transform-id)
    :cb-params {}}})

(defn fetch-pipeline-transform-callback [_ [error resp]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error "Failed to load transform"]
                  [:app.api/log :error "Failed to load transform" error]]}
    {:dispatch-n [[:pipeline-transform/add (->pipeline-transform resp)]
                  [:transform/add (->transform resp)]]}))

(reg-event-http
 :pipeline-editor.api/fetch-single-kioku-transform
 fetch-pipeline-transform-request
 fetch-pipeline-transform-callback)


(defn update-transform-request [_ [{:keys [id on-success]}]]
  (let [transform (-> @(re-posh/subscribe [:pipeline-transform/pull [:pipeline-transform/id id]])
                      utils/drop-ns-in-keys)
        {:keys [group-id tags input-tables output-tables]} transform
        unsaved-transform @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id id]])

        command (or (:pipeline-transform-unsaved/command unsaved-transform)
                    (:command transform))]
    {:http-xhrio
     {:url       (str "/api/transformations/" group-id "/transforms/" (:id transform))
      :method    :put
      :params    (-> transform
                     (dissoc :id :transform-id :master-id :cron :updated-at :updated-by :created-at :created-by :client-id :statements)
                     utils/clean-up-query-recursive
                     (update :tags select-tables tags)
                     (update :input-tables select-tables input-tables)
                     (update :output-tables select-tables output-tables)
                     (assoc :command command))
      :cb-params {:id id
                  :old-transform-id (:id transform)
                  :on-success       on-success}}}))

(defn update-transform-callback [_ [error resp {:keys [id on-success]}]]
  (cond
    (= 409 (:status error))
    {:dispatch-n [[:app.events/add-snack
                   :warning
                   "Someone has already updated this transform while you were editing it. Please save your changes, and reload the page to get the latest version."]
                  [:pipeline-transform/update [:pipeline-transform/id id] {:pipeline-transform/stale true}]]}

    (some? error)
    (let [message (or (get-in error [:response :message])
                      (get-in error [:response :error]))]
      {:dispatch-n [[:app.events/add-snack :error (if message
                                                    (str "Failed to update the transform.\n" message)
                                                    "Failed to update the transform")]
                    [:app.api/log :error "Failed to update the transform" error]]})

    :otherwise
    (let [transform (:data resp)]
      (when on-success (on-success))
      {:dispatch-n [[:app.events/add-snack :success "Transform updated"]
                    [:pipeline-editor.events/purge-local-transform id]
                    [:pipeline-transform/update [:pipeline-transform/id id] {:pipeline-transform/stale true}]
                    [:transformations.api/cyclic-deps-check
                     {:on-cycle-ev [:transform.events/toggle-circular-dependency-modal true transform]}]]})))

(reg-event-http
 :pipeline-editor.api/update-kioku-transform
 update-transform-request
 update-transform-callback)


(defn fetch-transform-preview-request [_ [{:keys [id command]}]]
  {:http-xhrio
   {:id        :fetch-transform-preview-request
    :method    :post
    :url       "/api/sink-console"
    :params    {:query command}
    :cb-params {:id id
                :command command}}})

(defn fetch-transform-preview-callback [_ [error {:keys [results]} {:keys [id command]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to run transform SQL preview." {:code (get-in error [:response :error])}]}
    {:dispatch [:pipeline-transform-preview/add (->pipeline-transform-preview
                                                 {:id      id
                                                  :command command
                                                  :results results})]}))

(reg-event-http
 :pipeline-editor.api/fetch-transform-preview
 fetch-transform-preview-request
 fetch-transform-preview-callback)


;;;;;;;;;;;;;;;;;;
;; Sandbox service
;;;;;;;;;;;;;;;;;;


(defn parse-service-transform [transform]
  (-> transform
      (assoc :id (:transform_id transform))
      (c.set/rename-keys
       {:master_id :master-id
        :transform_id :transform-id
        :group_id :group-id})))

(defn fetch-sandbox-transforms-request [{:keys [db]} {:keys [anchor-id]}]
  (let [client-id (get-in db [:app :client-id])
        anchor-id (when (and anchor-id (not= (get empty-tab :id) anchor-id)) anchor-id)]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/transforms"
                       {:client_id client-id})
      :params (when anchor-id {:anchor_id anchor-id})
      :method :GET}}))

(defn fetch-sandbox-transforms-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch sandbox transforms"]}
    (let [new-transforms (map-indexed (fn [idx item]
                                        (-> item parse-service-transform (assoc :order idx) ->sandbox-transform))
                                      resp)]

      {:dispatch-n [[:sandbox-transform/add-many new-transforms]
                    [:pipeline-editor.events/sync-tabs]]})))

(reg-event-http
 :sandbox.api/fetch-sandbox-transforms
 fetch-sandbox-transforms-request
 fetch-sandbox-transforms-callback)


(def search-default-items-per-page 20)

;; Twin of the kioku `/api/search` global search (minorodata.global-search.api),
;; but served by the pipeline-editor service (POST /clients/{client_id}/search)
;; so it sees the sandbox's entities. The service mirrors the global-search
;; response shape, so we reuse `:global-search/set-results` and the pipeline-editor
;; search UI reads from the same `:global-search/results`. Unlike the kioku
;; endpoint this one is a POST (query/types/pagination travel in the body) and
;; takes no `sort`.
(defn search-request [{:keys [db]} [{:keys [query types page items-per-page]}]]
  (when (>= (count query) 3)
    (let [client-id (get-in db [:app :client-id])]
      {:http-xhrio
       {:id     :sandbox-search
        :method :POST
        :url    (api-url "/pipeline-editor/clients/{client_id}/search"
                         {:client_id client-id})
        :params (assoc-some
                 {:query      (str/trim query)
                  :pagination {:items-per-page (or items-per-page search-default-items-per-page)
                               :page           (or page 1)}}
                 :types (into [] (not-empty (vec types))))}})))

(defn kebabize-keys
  "Recursively rewrite snake_case keyword keys to kebab-case. kioku kebab-cases
  its responses via an interceptor, but the pipeline-editor service returns
  snake_case, so we normalise here to match the shape the search UI and
  `:global-search/open-in-pipeline-editor` expect (e.g. `master_id` ->
  `master-id`, `group_name` -> `group-name`)."
  [data]
  (walk/postwalk
   (fn [x]
     (if (map? x)
       (into {}
             (map (fn [[k v]]
                    [(if (keyword? k) (keyword (str/replace (name k) "_" "-")) k) v]))
             x)
       x))
   data))

(defn search-callback [_ [error response]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Search failed. Please try again."]}
    {:dispatch [:global-search/set-results (kebabize-keys response)]}))

(reg-event-http
 :sandbox.api/search
 search-request
 search-callback)


(defn fetch-live-transforms-request [{:keys [db]} []]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url       (api-url "/pipeline-editor/clients/{client_id}/transforms/live"
                          {:client_id client-id})
      :method    :GET}}))

(defn fetch-live-transforms-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch transforms"]}
    {:dispatch [:live-transform/add-many (mapv (comp ->live-transform parse-service-transform) resp)]}))

(reg-event-http
 :sandbox.api/fetch-live-transforms
 fetch-live-transforms-request
 fetch-live-transforms-callback)


(defn evaluate-pipelines-request [{:keys [db]} []]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url       (api-url "/pipeline-editor/clients/{client_id}/evaluations"
                          {:client_id client-id})
      :method    :POST}}))

(defn evaluate-pipelines-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to evaluate pipeline"]}
    {:dispatch [:sandbox.events/watch-execution]}))

(reg-event-http
 :sandbox.api/evaluate-pipelines
 evaluate-pipelines-request
 evaluate-pipelines-callback)


(defn update-sandbox-transform-request [{:keys [db]} [{:keys [id updates]}]]
  (let [client-id (get-in db [:app :client-id])
        transform (-> @(re-posh/subscribe [:sandbox-transform/pull [:sandbox-transform/master-id id]])
                      utils/drop-ns-in-keys)
        unsaved-transform (-> @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id id]])
                              utils/drop-ns-in-keys)
        {:keys [command name description group-id]} (merge unsaved-transform updates)
        sql (or command (:sql transform))]
    {:http-xhrio
     {:id     (keyword (str "update-sandbox-transform-" id))
      :url    (api-url "/pipeline-editor/clients/{client_id}/transforms/{master_id}"
                       {:client_id client-id
                        :master_id id})
      :method :PUT
      :params {:name        (or name (:name transform))
               :description (or description (:description transform))
               :group_id    (or group-id (:group-id transform))
               :sql         sql}}}))

(defn update-sandbox-transform-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to update sandbox transform"
                {:code (get-in error [:response :detail])}]}
    (let [transform (-> resp parse-service-transform ->sandbox-transform)]
      {:dispatch-n [[:sandbox-transform/update [:sandbox-transform/master-id (:sandbox-transform/master-id transform)]
                     transform]
                    [:pipeline-editor.events/sync-tabs]
                    [:app.events/add-snack :success "Transform updated"]]
       :posthog/capture {:event (:sandbox/transform-updated posthog-events/events)}})))

(reg-event-http
 :sandbox.api/update-sandbox-transform
 update-sandbox-transform-request
 update-sandbox-transform-callback)


(reg-event-fx
 :sandbox.api/update-transform
 (fn [_ [_ props]]
   (if @(re-posh/subscribe [:sandbox.subs/enabled?])
     {:dispatch [:sandbox.events/update-transform props]}
     {:dispatch [:pipeline-editor.api/update-kioku-transform props]})))


(defn create-sandbox-transform-request [{:keys [db]} [{:keys [group-id transform]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/transforms"
                       {:client_id client-id})
      :method :POST
      :params (-> transform
                  (assoc :group_id group-id)
                  (c.set/rename-keys {:command :sql})
                  (select-keys [:name :description :sql :group_id :tags])
                  (update :sql #(or % "")))}}))

(defn create-sandbox-transform-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to create sandbox transform"]}
    (let [transform (-> resp parse-service-transform ->sandbox-transform)]
      {:dispatch-n [[:sandbox-transform/add transform]
                    [:sandbox.api/evaluate-pipelines]
                    [:sandbox.events/watch-execution]
                    [:sandbox.api/fetch-sandbox-updates]
                    [:app.events/add-snack :success "Transform created"]]
       :posthog/capture {:event (:sandbox/transform-created posthog-events/events)}})))

(reg-event-http
 :sandbox.api/create-sandbox-transform
 create-sandbox-transform-request
 create-sandbox-transform-callback)

(defn delete-sandbox-transform-request [{:keys [db]} [{:keys [id]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/transforms/{master_id}"
                       {:client_id client-id
                        :master_id id})
      :method :DELETE
      :cb-params {:id id}}}))

(defn delete-sandbox-transform-callback [_ [error _ {:keys [id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to delete sandbox transform"]}
    {:dispatch-n [[:sandbox-transform/delete [:sandbox-transform/master-id id]]
                  [:sandbox.api/evaluate-pipelines]
                  [:sandbox.events/watch-execution]]}))

(reg-event-http
 :sandbox.api/delete-sandbox-transform
 delete-sandbox-transform-request
 delete-sandbox-transform-callback)


(defn parse-update-item [{:keys [type master_id group_id previous_id user_id transform]}]
  {:type        type
   :master-id   master_id
   :group-id    group_id
   :previous-id previous_id
   :user-id     user_id
   :transform   (some-> transform parse-service-transform)})

(defn parse-version [v]
  (-> v
      (c.set/rename-keys
       {:version_id :version-id
        :created_at :created-at
        :user_id    :user-id
        :user_name  :user-name})
      (assoc :created-by (:user_id v))
      (update :revisions (fn [rvs] (mapv parse-update-item rvs)))))

(defn fetch-versions-request [{:keys [db]} []]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url       (api-url "/pipeline-editor/clients/{client_id}/versions"
                          {:client_id client-id})
      :method    :GET}}))

(defn fetch-versions-callback [_ [error resp]]
  (let [current @(re-posh/subscribe [:sandbox-version/pull-many])
        new-versions (mapv (comp ->sandbox-version parse-version) resp)
        new-ids   (set (mapv :sandbox-version/version-id new-versions))
        purge-ids (keep (fn [{:sandbox-version/keys [version-id]}]
                          (when-not (new-ids version-id) version-id))
                        current)]
    (if (some? error)
      {:dispatch [:app.events/add-snack :error "Failed to fetch sandbox versions"]}
      {:dispatch-n (concat [[:sandbox-version/add-many new-versions]]
                           (mapv #(-> [:sandbox-version/delete [:sandbox-version/version-id %]]) purge-ids))})))

(reg-event-http
 :sandbox.api/fetch-versions
 fetch-versions-request
 fetch-versions-callback)


(defn- update-ids-props [update-ids]
  (if (seq update-ids)
    {:scope "selection" :transform-count (count update-ids)}
    {:scope "all"}))

(defn stash-version-request [{:keys [db]} [{:keys [comment update-ids]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/versions"
                       {:client_id client-id})
      :method :POST
      :params (cond-> {:comment comment}
                (seq update-ids)
                (assoc :update_ids update-ids))
      :cb-params (update-ids-props update-ids)}}))

(defn stash-version-callback [_ [error _ props]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to store sandbox version"]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox version stored successfully"]
                  [:sandbox.events/watch-execution]]
     :posthog/capture {:event (:sandbox/transform-stashed posthog-events/events)
                       :props props}}))

(reg-event-http
 :sandbox.api/stash-version
 stash-version-request
 stash-version-callback)


(defn load-version-request [{:keys [db]} [{:keys [version-id]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/versions/{version_id}"
                       {:client_id client-id
                        :version_id version-id})
      :method :POST}}))

(defn load-version-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail])
                {:title "Failed to load sandbox version"}]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox version loaded successfully"]
                  [:sandbox.events/watch-execution]]
     :posthog/capture {:event (:sandbox/version-loaded posthog-events/events)}}))

(reg-event-http
 :sandbox.api/load-version
 load-version-request
 load-version-callback)


(defn reset-sandbox-request [{:keys [db]} [{:keys [update-ids]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/reset"
                       {:client_id client-id})
      :method :POST
      :params (assoc-some {} :update_ids update-ids)
      :cb-params (update-ids-props update-ids)}}))

(defn reset-sandbox-callback [_ [error _ props]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail])
                {:title "Failed to reset sandbox"}]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox reset successfully"]
                  [:sandbox.events/watch-execution]]
     :posthog/capture {:event (:sandbox/reset posthog-events/events)
                       :props props}}))

(reg-event-http
 :sandbox.api/reset-sandbox
 reset-sandbox-request
 reset-sandbox-callback)


(defn reset-sandbox-transform-request [{:keys [db]} [{:keys [id]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/transforms/{master_id}/reset"
                       {:client_id client-id
                        :master_id id})
      :method :POST}}))

(defn reset-sandbox-transform-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail])
                {:title "Failed to reset sandbox transform"}]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox transform reset successfully"]
                  [:sandbox.events/watch-execution]]}))


(reg-event-http
 :sandbox.api/reset-sandbox-transform
 reset-sandbox-transform-request
 reset-sandbox-transform-callback)


(defn rebase-version-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/rebase"
                       {:client_id client-id})
      :method :POST}}))

(defn rebase-version-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail])
                {:title "Failed to rebase sandbox version"}]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox version rebase successfully"]
                  [:sandbox.events/watch-execution]]
     :posthog/capture {:event (:sandbox/sync posthog-events/events)}}))

(reg-event-http
 :sandbox.api/rebase-version
 rebase-version-request
 rebase-version-callback)


(defn commit-version-request [{:keys [db]} [{:keys [comment update-ids]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/commit"
                       {:client_id client-id})
      :method :POST
      :params (cond-> {:comment comment}
                (seq update-ids)
                (assoc :update_ids update-ids))
      :cb-params (update-ids-props update-ids)}}))

(defn commit-version-callback [_ [error _ props]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail] "")
                {:title "Failed to commit sandbox"}]}
    {:dispatch-n [[:app.events/add-snack :success "Sandbox version committed successfully"]
                  [:sandbox.events/watch-execution]]
     :posthog/capture {:event (:sandbox/transform-committed posthog-events/events)
                       :props props}}))

(reg-event-http
 :sandbox.api/commit-version
 commit-version-request
 commit-version-callback)


(defn parse-revision [item]
  (c.set/rename-keys item
                     {:master_id   :master-id
                      :user_id     :user-id
                      :previous_id :previous-id
                      :group_id    :group-id}))

(defn parse-commit [item]
  (-> item
      (c.set/rename-keys
       {:commit_id  :commit-id
        :created_at :created-at
        :user_id    :created-by})
      (update :revision (fn [rvs] (mapv parse-revision rvs)))))

(defn fetch-commits-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/commits"
                       {:client_id client-id})
      :method :GET}}))

(defn fetch-commits-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail] "")
                {:title "Failed to fetch sandbox commits"}]}
    {:dispatch [:commit/add-many (map-indexed (fn [idx itm] (->commit (assoc (parse-commit itm) :order idx)))
                                              resp)]}))

(reg-event-http
 :sandbox.api/fetch-commits
 fetch-commits-request
 fetch-commits-callback)


(defn delete-version-request [{:keys [db]} [{:keys [version-id]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/versions/{version_id}"
                       {:client_id client-id
                        :version_id version-id})
      :method :DELETE}}))

(defn delete-version-callback [_ [error]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to delete sandbox version"]}
    {:dispatch [:app.events/add-snack :success "Sandbox version deleted successfully"]}))

(reg-event-http
 :sandbox.api/delete-version
 delete-version-request
 delete-version-callback)


(defn parse-service-table [item]
  (let [[table schema catalog] (reverse (str/split (:name item) #"\."))]
    (when (and table schema catalog)
      {:id (:name item)
       :table table
       :schema schema
       :catalog catalog
       :columns (mapv #(-> {:column (or (:column %) (:column_name %))
                            :type   (or (:type %) (:data_type %))})
                      (:columns item))})))

(defn fetch-sandbox-tables-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/tables"
                       {:client_id client-id})
      :method :GET}}))

(defn fetch-sandbox-tables-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch sandbox tables"]}
    {:dispatch [:sandbox-table/add-many (keep (comp ->sandbox-table parse-service-table) resp)]}))

(reg-event-http
 :sandbox.api/fetch-sandbox-tables
 fetch-sandbox-tables-request
 fetch-sandbox-tables-callback)


(defn fetch-sandbox-table-request [{:keys [db]} [{:keys [params]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:method    :GET
      :url       (api-url "/pipeline-editor/clients/{client_id}/tables/{table_id}"
                          {:client_id client-id
                           :table_id  (:id params)})
      :cb-params {:params params}}}))

(defn fetch-sandbox-table-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch the table"]}
    (let [table (parse-service-table resp)]
      {:dispatch-n [[:sandbox-table/add (->sandbox-table table)]
                    [:pipeline-table/add (->pipeline-table table)]]})))

(reg-event-http
 :sandbox.api/fetch-sandbox-table
 fetch-sandbox-table-request
 fetch-sandbox-table-callback)

(reg-event-fx
 :sandbox.api/fetch-table
 (fn [_ [_ props]]
   (if @(re-posh/subscribe [:sandbox.subs/enabled?])
     {:dispatch [:sandbox.api/fetch-sandbox-table props]}
     {:dispatch [:pipeline-editor.api/fetch-live-table props]})))


(defn fetch-sandbox-table-preview-request [{:keys [db]} [{:keys [params]}]]
  (let [client-id (get-in db [:app :client-id])
        row-count (get-in db [:pipeline-editor :panel :row-count])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/tables/{table_id}/preview"
                       {:client_id client-id
                        :table_id  (:id params)})
      :method :GET
      :params {:max_rows row-count}
      :cb-params params}}))

(defn fetch-sandbox-table-preview-callback [_ [error resp {:keys [id]}]]
  (if (some? error)
    {:dispatch-n [[:app.events/add-snack :error (get-in error [:response :detail] "") {:title "Failed to preview table"}]
                  [:pipeline-table-preview/add (->pipeline-table-preview
                                                {:id      id
                                                 :error   (get-in error [:response :detail] "Failed to preview table")})]]}
    {:dispatch [:pipeline-table-preview/add (->pipeline-table-preview
                                             {:id      id
                                              :headers (first resp)
                                              :rows    (into [] (next resp))})]}))

(reg-event-http
 :sandbox.api/fetch-sandbox-table-preview
 fetch-sandbox-table-preview-request
 fetch-sandbox-table-preview-callback)


(reg-event-fx
 :sandbox.api/fetch-table-preview
 (fn [_ [_ props]]
   (if @(re-posh/subscribe [:sandbox.subs/enabled?])
     {:dispatch [:sandbox.api/fetch-sandbox-table-preview props]}
     {:dispatch [:pipeline-editor.api/fetch-live-table-preview props]})))

(defn fetch-sandbox-updates-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/updates"
                       {:client_id client-id})
      :method :GET}}))

(defn fetch-sandbox-updates-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch sandbox updates"]}
    {:dispatch [:sandbox-updates/update (->sandbox-updates {:updates (mapv parse-update-item resp)})]}))

(reg-event-http
 :sandbox.api/fetch-sandbox-updates
 fetch-sandbox-updates-request
 fetch-sandbox-updates-callback)


(defn preview-sandbox-sql-request [{:keys [db]} [{:keys [id sql]}]]
  (let [client-id (get-in db [:app :client-id])
        row-count (get-in db [:pipeline-editor :panel :row-count])]
    {:http-xhrio
     {:url    (str (api-url "/pipeline-editor/clients/{client_id}/query/preview" {:client_id client-id})
                   (when row-count (str "?max_rows=" row-count)))
      :method :POST
      :params {:query sql}
      :cb-params {:id  id
                  :sql sql}}}))

(defn preview-sandbox-sql-callback [_ [error resp {:keys [id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error (get-in error [:response :detail]) {:title "Failed to preview table"}]}
    {:dispatch-n [[:sandbox-preview/add (->sandbox-preview {:id id
                                                            :results resp})]
                  [:sandbox.events/refresh-sandbox-tables]]}))

(reg-event-http
 :sandbox.api/preview-sandbox-sql
 preview-sandbox-sql-request
 preview-sandbox-sql-callback)

(defn parse-execution [{:keys [transform status error]}]
  (->sandbox-transform-execution
   {:master-id (:master_id transform)
    :status status
    :error  error}))

(defn fetch-execution-statuses-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/jobs"
                       {:client_id client-id})
      :method :GET}}))

(defn fetch-execution-statuses-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch execution statuses"]}
    (let [old-executions @(re-posh/subscribe [:sandbox-transform-execution/pull-many])
          ids (mapv :sandbox-transform-execution/master-id old-executions)]
      {:dispatch-n (concat (mapv #(-> [:sandbox-transform-execution/delete [:sandbox-transform-execution/master-id %]]) ids)
                           [[:sandbox-transform-execution/add-many (mapv parse-execution resp)]])})))

(reg-event-http
 :sandbox.api/fetch-execution-statuses
 fetch-execution-statuses-request
 fetch-execution-statuses-callback)


(defn maybe-create-sandbox-request [{:keys [db]}]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/sandbox"
                       {:client_id client-id})
      :method :PUT}}))

(defn maybe-create-sandbox-callback [{:keys [db]} [error resp]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to initialise sandbox"]}
    {:db (assoc-in db
                   [:pipeline-editor :sandbox-metadata]
                   (c.set/rename-keys resp {:created_at :created-at}))}))

(reg-event-http
 :sandbox.api/maybe-create-sandbox
 maybe-create-sandbox-request
 maybe-create-sandbox-callback)



(defn fetch-live-dag-request [_ [{:keys [anchor-id anchor-type]}]]
  {:http-xhrio
   {:url    (api-url "/api/dependency-graph/live?anchor_id={anchor_id}&anchor_type={anchor_type}"
                     {:anchor_id anchor-id
                      :anchor_type anchor-type})
    :cb-params {:anchor-id anchor-id}}})

(defn fetch-live-dag-callback
  "Fetch results are layout within async-flow"
  [_ [error _resp {:keys [anchor-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch DAG"]}
    (when anchor-id
      {:dispatch [:pipeline-dag/add (->pipeline-dag {:id (get-dag-id anchor-id false)
                                                     :filters {:direction "all"}})]})))

(reg-event-http
 :sandbox.api/fetch-live-dag
 fetch-live-dag-request
 fetch-live-dag-callback)


(defn fetch-sandbox-dag-request [{:keys [db]} [{:keys [anchor-id anchor-type]}]]
  (let [client-id (get-in db [:app :client-id])]
    {:http-xhrio
     {:url    (api-url "/pipeline-editor/clients/{client_id}/dag?anchor_id={anchor_id}&anchor_type={anchor_type}"
                       {:client_id client-id
                        :anchor_id anchor-id
                        :anchor_type anchor-type})
      :cb-params {:anchor-id anchor-id}}}))

(defn fetch-sandbox-dag-callback
  "Fetch results are layout within async-flow"
  [_ [error _resp {:keys [anchor-id]}]]
  (if (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to fetch DAG"]}
    (when anchor-id
      {:dispatch [:pipeline-dag/add
                  (->pipeline-dag {:id (get-dag-id anchor-id true)
                                   :filters {:direction "all"}})]})))

(reg-event-http
 :sandbox.api/fetch-sandbox-dag
 fetch-sandbox-dag-request
 fetch-sandbox-dag-callback)
